import { useMemo } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useIxtE2eV1TestRunQueryServiceTestRunGet } from '@interxfi/api-client/generated/hooks/ixt-e2e-v1-test-run-query-service-test-run-get';
import { PageHeader } from '@interxfi/app-components/components/layout/page-header/page-header';
import {
  DEFAULT_PAGE_LAYOUT_GAP,
  DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE,
  PageLayout,
} from '@interxfi/app-components/components/layout/page-layout/page-layout';
import { AppReadonlyList } from '@interxfi/app-components/components/readonly-list/app-readonly-list';
import { BoxedAppReadonlyList } from '@interxfi/app-components/components/readonly-list/boxed-app-readonly-list';
import { type AggregateBreadcrumb, buildBreadcrumb } from '@interxfi/app-components/components/router-breadcrumbs/router-breadcrumbs';
import { HorizontalStack } from '@interxfi/design-system/components/alignment/horizontal-stack';
import { VerticalStack } from '@interxfi/design-system/components/alignment/vertical-stack';
import { CopyButton } from '@interxfi/design-system/components/copy-button/copy-button';
import { getShortenedId } from '@interxfi/design-system/components/format/id/shared';
import { LAYOUT_ZERO_CONTAINER_PADDING } from '@interxfi/design-system/components/layout-container/layout-container';
import {
  DEFAULT_WRAPPING_LAYOUT_COLUMN,
  WrappingLayoutContainer,
} from '@interxfi/design-system/components/layout-container/wrapping/wrapping-layout-container';
import type { ReadonlyListItem } from '@interxfi/design-system/components/list/readonly/item/readonly-list-item';
import { Typography } from '@interxfi/design-system/components/typography';
import type { IxtE2eV1TestRunState } from '@interxfi/types';
import { TestRunEntityMenu } from '../../../../../../../../features/o5/e2e/test-run-entity-menu';
import { E2eTestRunEventSummary } from '../../../../../../../../features/o5/e2e/test-run-event';
import { getE2eTestRunStateListItems } from '../../../../../../../../features/o5/e2e/test-run-list-items';
import { E2eTestRunStatusPill } from '../../../../../../../../features/o5/e2e/test-run-status-pill';
import { buildDefaultErrorMessageGetter, useErrorHandler } from '../../../../../../../../util/error-handler';
import { getPageHead } from '../../../../../../../../util/routing/meta';

export const Route = createFileRoute('/_app/_o5/_o5-required/tech-ops/test-run/_test-run/$environment/$testRunId')({
  loader: ({ params }) =>
    ({
      breadcrumb: buildBreadcrumb({
        id: `e2e-test-run-${params.environment}-${params.testRunId}`,
        label: `Test Run ${getShortenedId(params.testRunId)}`,
        to: `/tech-ops/test-run/${params.environment}/${params.testRunId}`,
        suffix: <CopyButton content={params.testRunId} label="Copy Test Run ID" tooltip={{ copy: params.testRunId, copied: 'Copied Test Run ID' }} />,
      }),
    }) satisfies AggregateBreadcrumb,
  head: () => getPageHead('E2E Test Run'),
  component: E2eTestRunDetail,
});

const errorMessageGetter = buildDefaultErrorMessageGetter('Failed to load test run', 'view this test run');

function getListItems(data: IxtE2eV1TestRunState | undefined) {
  const items = getE2eTestRunStateListItems(data);

  return {
    left: [
      items.data.submittedAt,
      items.data.stoppedAt,
      ...(items.data.errorMessage ? [items.data.errorMessage] : []),
    ] as const satisfies ReadonlyListItem[],
    right: [items.metadata.createdAt, items.metadata.updatedAt, items.testRunId, items.environment] as const satisfies ReadonlyListItem[],
  } as const;
}

function E2eTestRunDetail() {
  const { environment, testRunId } = Route.useParams();
  const request = { environment, testRunId } as const;
  const { data, error, isPending } = useIxtE2eV1TestRunQueryServiceTestRunGet(request);
  useErrorHandler(error, errorMessageGetter);
  const showPending = Boolean(isPending || error);
  const listItems = useMemo(() => getListItems(data?.testRun), [data?.testRun]);

  return (
    <PageLayout maxWidthContainer={DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE}>
      <PageLayout.Section noBorder>
        <PageHeader>
          <HorizontalStack gap={2} wrap="nowrap">
            <E2eTestRunStatusPill isPending={showPending} size="large" status={data?.testRun?.status} />
          </HorizontalStack>
          <TestRunEntityMenu environment={environment} testRunId={testRunId} />
        </PageHeader>
      </PageLayout.Section>

      <PageLayout.Section>
        <VerticalStack gap={4}>
          <Typography.Level3>Events</Typography.Level3>

          <E2eTestRunEventSummary gap={DEFAULT_PAGE_LAYOUT_GAP} padding={LAYOUT_ZERO_CONTAINER_PADDING} request={request} />
        </VerticalStack>
      </PageLayout.Section>

      <PageLayout.Section>
        <WrappingLayoutContainer
          gap={DEFAULT_PAGE_LAYOUT_GAP}
          left={{
            ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
            render: <AppReadonlyList isPending={showPending} items={listItems.left} />,
          }}
          padding={LAYOUT_ZERO_CONTAINER_PADDING}
          right={{
            ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
            render: <BoxedAppReadonlyList isPending={showPending} items={listItems.right} />,
          }}
          wrap="forward"
          wrapGap={7}
        />
      </PageLayout.Section>
    </PageLayout>
  );
}
